import { FC, useEffect, useRef, useState } from 'react'
import { UserAuth } from '../../../../../context/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react'
import { FormCreateUserAccount } from './FormCreateUser'
import { ResentValidationEmail } from './FormResendValidationEmail'
import { StepForm2, StepForm4 } from '../components'
import { SignUpAssistant } from '../components/SignUpAssistant'
import './SignUpLayout.css'
import { REGISTRATION_STATUS } from '../../../../../utils/authSession'

const stepTitles = [
  { title: '¡Vamos a crear una cuenta!', subtitle: 'Estás a pocos minutos de automatizar tu empresa.' },
  {title: 'Crea tu espacio en TryLuca',subtitle: 'Aquí podrás agrupar una o más empresas en un solo lugar. Puedes empezar con una y sumar más después.',},
  //{title: 'Conecta tu empresa a TryLuca',subtitle: 'Selecciona tu rol para personalizar tu experiencia en la plataforma.',},
  {title: 'Conecta tu primera empresa',subtitle: 'En pocos minutos tendrás tu información tributaria sincronizada y tu primer reporte listo.',},
  { title: 'Confirma tu correo para continuar', subtitle: '' },
]

const stepLabels = [
  "Empecemos contigo...",
  "Configuración inicial",
  "Conexión",
  "Verificación",
];

export const SignUpPage: FC = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const { registrationStatus } = UserAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const swiperRef = useRef<SwiperRef>(null)
  const steps = [
    <FormCreateUserAccount key={0} />,
    <StepForm2 key={1} />,
    <StepForm4 key={2} />,
    <ResentValidationEmail key={3} isActive={activeIndex === 3} />,
  ]

  useEffect(() => {
    if (!swiperRef.current) return

    const requestedStep = searchParams.get('step')
    const requestedIndex = {
      profile: 0,
      organization: 1,
      verification: 3,
      '2': 1
    }[requestedStep || '']
    const statusIndex = registrationStatus
      ? {
          [REGISTRATION_STATUS.PROFILE_PENDING]: 0,
          [REGISTRATION_STATUS.ORGANIZATION_PENDING]: 1,
          [REGISTRATION_STATUS.EMAIL_VERIFICATION_PENDING]:
            requestedStep === 'verification' ? 3 : undefined
        }[registrationStatus]
      : undefined

    if (registrationStatus === REGISTRATION_STATUS.COMPLETED) {
      navigate(
        requestedStep === 'verification' ? '/complete-signup/poll-sii' : '/',
        { replace: true }
      )
      return
    }

    if (
      registrationStatus === REGISTRATION_STATUS.EMAIL_VERIFICATION_PENDING &&
      requestedStep !== 'verification'
    ) {
      return
    }

    swiperRef.current.swiper.slideTo(statusIndex ?? requestedIndex ?? 0)
  }, [navigate, registrationStatus, searchParams])

  return (
    <div className='signup-wrapper w-full'>

      {/* 🔹 STEP HEADER estilo imagen */}
    <div className="signup-section flex items-center gap-3 mb-6 w-full">
      <div className="w-full">
        <div className="signup-step-label w-full flex items-center justify-center bg-gray-100 px-3 py-1 rounded-full transition-colors duration-200 hover:bg-accent2 hover:text-white cursor-pointer group">
         <span className="text-sm sm:text-lg md:text-2xl font-medium text-center text-gray-600 group-hover:text-white transition-colors duration-200">
            Paso {activeIndex + 1} de {stepTitles.length} – {stepLabels[activeIndex]}
          </span>
        </div>
      </div>
    </div>

    <div className="signup-section w-full flex flex-col items-center text-center">
      <h2 className="signup-title text-4xl font-bold text-white lg:text-primary mb-2">
        {stepTitles[activeIndex]?.title}
      </h2>
      <p className="signup-subtitle text-white/70 lg:text-gray-500 mb-8 max-w-md">
        {stepTitles[activeIndex]?.subtitle}
      </p>
    </div>

    {/* 🔹 FORM (SIN CARD PESADA) */}
    <div className="signup-container
  relative w-full max-w-xl p-8 
  bg-white/10 lg:bg-white 
  backdrop-blur-md 
  rounded-2xl shadow-full 

  border border-white         /* MOBILE */
  lg:border-none              /* DESKTOP */
">
      <Swiper
        className='signup-swiper'
        ref={swiperRef}
        onSlideChange={(sw) => setActiveIndex(sw.activeIndex)}
        spaceBetween={30}
        autoHeight
        allowTouchMove={false}
      >
        {steps.map((step, index) => (
          <SwiperSlide key={index}>{step}</SwiperSlide>
        ))}
      </Swiper>
    </div>

    {/* 🔹 FOOTER */}
    <p className='signup-footer text-xs mt-6 text-white/60 lg:text-gray-400 text-center'>
      ¿Necesitas ayuda? Escríbenos a{" "}
      <span className='text-white lg:text-accent1'>soporte@tryluca.cl</span>
    </p>

    <SignUpAssistant currentStep={activeIndex} />

  </div>
)

}
